import React, { useContext, useState } from "react";
import { Modal, TableRow } from "@mui/material";
import { Visibility as PreviewIcon } from "@mui/icons-material";
import {
    ButtonRow,
    Expandable,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
} from "../../../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../../../MaterialOrder/Styled";
import { api } from "../../../../../shared/hooks/api";
import { error, success } from "../../../../../utils/toast";
import { ROLE } from "../../../../../data/enums";
import { AuthenticationContext } from "../../../../../provider/AuthenticationProvider";
import LoadingButton from "../../../../../shared/components/LoadingButton";
import DocxPreviewModal from "../../../../../shared/components/DocxPreviewModal";
import { generateDocumentBlob } from "../../../../../utils/exportUtils";
import { buildDocxData } from "../../../../../utils/docxBuilder";
import { productExportPreviewDescriptor } from "../../../../../utils/docxMappings";
import ExportTemplate from "../../../../../templates/PMS - Phiếu xuất kho thành phẩm.docx";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../../shared/components/TextBox";
import DeleteIcon from "../../../../../shared/svg/DeleteIcon";
import IconButton from "../../../../../shared/components/IconButton";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../../utils/format";
import { ExportProductForm } from "../../../../Products/Title/ExportProductModal/ExportProductForm";

interface PrefillProductItem {
    id: string;
    name: string;
    exportQuantity: number;
    note?: string;
}

interface ExportDeliveryTripModalProps {
    open: boolean;
    handleClose: () => void;
    initialFormData?: any;
    initialItems?: PrefillProductItem[];
    onExported?: () => void;
    deliveryTripId: string;
}

const initialData = () => ({
    receiver_name: "",
    address: "",
    customer: null,
    reason: "",
    storage: "",
    note: "",
    products: [],
});

const TableColumns = [
    { text: "Sản phẩm/vật tư", icon: null },
    { text: "Số lượng xuất", icon: null },
    { text: "Ghi chú", icon: null },
    { text: "", icon: null },
];

const ProductRow = ({ item, extraRowProps }: any) => (
    <TableRow>
        <StyledTableCell>{item.name}</StyledTableCell>
        <StyledTableCell>
            <TextBox
                numeric
                style={{ width: 80 }}
                text={formatInternationalNumber(item.exportQuantity ?? "")}
                onChangeText={(value: string) => {
                    const n = parseInternationalToNumber(value, error);
                    if (!Number.isFinite(n)) return;
                    extraRowProps.onChangeExportQuantity(item.id, n);
                }}
                disabled={!!extraRowProps?.disableExportQuantity}
            />
        </StyledTableCell>
        <StyledTableCell>
            <TextBox
                text={item.note || ""}
                onChangeText={(value: string) => extraRowProps.onChangeNote(item.id, value)}
                style={{ width: 150 }}
            />
        </StyledTableCell>
        <StyledTableCell>
            <IconButton onClick={() => extraRowProps.removeSelectedItem(item.id)}>
                <DeleteIcon />
            </IconButton>
        </StyledTableCell>
    </TableRow>
);

export const ExportDeliveryTripModal = ({
                                            open,
                                            handleClose,
                                            initialFormData,
                                            initialItems,
                                            onExported,
                                            deliveryTripId,
                                        }: ExportDeliveryTripModalProps) => {
    const { user: { role } } = useContext(AuthenticationContext);
    const [isOpen, setIsOpen] = useState(true);
    const [formData, setFormData] = useState<any>({
        ...initialData(),
        ...(initialFormData || {}),
    });
    const [selectedProducts, setSelectedProducts] = useState<any[]>(Array.isArray(initialItems) ? initialItems as any[] : []);
    const [{ isCreating }, createDeliveryTripExport] = api.post(`/product-flow/v2/delivery-trips/${deliveryTripId}/export/`);

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
    const mergeFormData = (patch: any) => setFormData((prev: any) => ({ ...prev, ...patch }));

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    const onChangeExportQuantity = (id: string, quantity: number) => {
        setSelectedProducts(prev => prev.map(p => p.id === id ? { ...p, exportQuantity: quantity } : p));
    };
    const onChangeNote = (id: string, note: string) => {
        setSelectedProducts(prev => prev.map(p => p.id === id ? { ...p, note } : p));
    };
    const removeSelectedItem = (id: string) => {
        setSelectedProducts(prev => prev.filter((p: any) => p.id !== id));
    };

    const handleExport = async () => {
        try {
            const response = await createDeliveryTripExport({ delivery_trip_id: deliveryTripId });
            let message = "Xuất kho sản phẩm/vật tư thành công";
            if (response?.exported_storages && Array.isArray(response.exported_storages)) {
                message += `. Kho đã xuất: ${response.exported_storages.join(", ")}`;
            }
            success("THÀNH CÔNG", message);
            onExported && onExported();
            setIsPreviewOpen(false);
            setPreviewBlob(null);
            handleCloseWithAnimation();
        } catch (err) {
            error("Không thể xuất kho", err);
        }
    };

    const validateExportData = () => {
        const errors: string[] = [];
        if (!formData.customer) errors.push("Khách hàng không được để trống.");
        if (!formData.reason) errors.push("Lý do xuất không được để trống.");
        if (!selectedProducts || selectedProducts.length === 0) errors.push("Danh sách sản phẩm không được để trống.");
        return { isValid: errors.length === 0, errors };
    };

    const handlePreview = async () => {
        const validation = validateExportData();
        if (!validation.isValid) {
            alert("Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" + validation.errors.join("\n\n"));
            return;
        }
        try {
            const data = buildDocxData(
                { formData, selectedProducts },
                productExportPreviewDescriptor,
            );
            const blob = await generateDocumentBlob(data, ExportTemplate);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate product export preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const disableQuantityInput = [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseWithAnimation}
                closeAfterTransition
                BackdropComponent={StyledBackdrop}
                BackdropProps={{
                    timeout: 500,
                    onClick: (e) => e.stopPropagation(),
                }}
            >
                <ModalWrapper isOpen={isOpen}>
                    <Expandable>
                        {/* Form section (requested for DeliveryTrip modals) */}
                        <ExportProductForm
                            formData={formData}
                            onChange={mergeFormData}
                            lockAddress
                        />

                        <TitleText>Danh sách xuất kho sản phẩm/vật tư</TitleText>

                        <CustomTable
                            itemList={selectedProducts}
                            TableColumns={TableColumns}
                            RowElement={ProductRow}
                            extraRowProps={{
                                onChangeExportQuantity,
                                onChangeNote,
                                removeSelectedItem,
                                disableExportQuantity: disableQuantityInput,
                            }}
                        />
                    </Expandable>

                    <ButtonRow>
                        <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                        <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">Xem trước
                            phiếu</StyledButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu xuất kho thành phẩm"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={handleExport}
                        disabled={isCreating}
                        isLoading={isCreating}
                        text="Xuất kho"
                    />
                }
            />
        </>
    );
};

export default ExportDeliveryTripModal;
